// Environment configuration and validation

// Environment variables with defaults and validation
const ENV_CONFIG = {
  // API Configuration
  BACKEND_URL: {
    value: import.meta.env.VITE_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL,
    required: true,
    description: 'Backend API URL or Supabase Functions URL'
  },
  
  // Supabase Configuration
  SUPABASE_URL: {
    value: import.meta.env.VITE_SUPABASE_URL || import.meta.env.REACT_APP_SUPABASE_URL,
    required: false,
    description: 'Supabase project URL'
  },
  
  SUPABASE_ANON_KEY: {
    value: import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.REACT_APP_SUPABASE_ANON_KEY,
    required: false,
    description: 'Supabase anonymous key'
  },
  
  // Application Configuration
  APP_NAME: {
    value: import.meta.env.VITE_APP_NAME || import.meta.env.REACT_APP_NAME || 'Dashboard Gallery',
    required: false,
    description: 'Application name'
  },
  
  APP_VERSION: {
    value: import.meta.env.VITE_APP_VERSION || import.meta.env.REACT_APP_VERSION || '1.0.0',
    required: false,
    description: 'Application version'
  },
  
  // Feature Flags
  ENABLE_ANALYTICS: {
    value: (import.meta.env.VITE_ENABLE_ANALYTICS || import.meta.env.REACT_APP_ENABLE_ANALYTICS) === 'true',
    required: false,
    description: 'Enable Google Analytics'
  },
  
  ENABLE_ERROR_REPORTING: {
    value: (import.meta.env.VITE_ENABLE_ERROR_REPORTING || import.meta.env.REACT_APP_ENABLE_ERROR_REPORTING) === 'true',
    required: false,
    description: 'Enable error reporting'
  },
  
  ENABLE_CACHING: {
    value: (import.meta.env.VITE_ENABLE_CACHING || import.meta.env.REACT_APP_ENABLE_CACHING) !== 'false', // Default to true
    required: false,
    description: 'Enable API response caching'
  },
  
  // Performance Configuration
  API_TIMEOUT: {
    value: parseInt(import.meta.env.VITE_API_TIMEOUT || import.meta.env.REACT_APP_API_TIMEOUT) || 30000,
    required: false,
    description: 'API request timeout in milliseconds'
  },
  
  API_RETRY_ATTEMPTS: {
    value: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || import.meta.env.REACT_APP_API_RETRY_ATTEMPTS) || 3,
    required: false,
    description: 'Number of API retry attempts'
  },
  
  CACHE_TIMEOUT: {
    value: parseInt(import.meta.env.VITE_CACHE_TIMEOUT || import.meta.env.REACT_APP_CACHE_TIMEOUT) || 300000, // 5 minutes
    required: false,
    description: 'Cache timeout in milliseconds'
  },
  
  // Image Configuration
  MAX_IMAGE_SIZE: {
    value: parseInt(import.meta.env.VITE_MAX_IMAGE_SIZE || import.meta.env.REACT_APP_MAX_IMAGE_SIZE) || 5242880, // 5MB
    required: false,
    description: 'Maximum image upload size in bytes'
  },
  
  SUPPORTED_IMAGE_FORMATS: {
    value: import.meta.env.VITE_SUPPORTED_IMAGE_FORMATS || import.meta.env.REACT_APP_SUPPORTED_IMAGE_FORMATS || 'jpg,jpeg,png,webp,gif',
    required: false,
    description: 'Comma-separated list of supported image formats'
  },
  
  // Development Configuration
  DEBUG_MODE: {
    value: (import.meta.env.VITE_DEBUG || import.meta.env.REACT_APP_DEBUG) === 'true' || import.meta.env.MODE === 'development',
    required: false,
    description: 'Enable debug mode'
  },
  
  LOG_LEVEL: {
    value: import.meta.env.VITE_LOG_LEVEL || import.meta.env.REACT_APP_LOG_LEVEL || (import.meta.env.MODE === 'development' ? 'debug' : 'error'),
    required: false,
    description: 'Logging level (debug, info, warn, error)'
  }
};

// Validate required environment variables
const validateEnvironment = () => {
  const errors = [];
  const warnings = [];
  
  Object.entries(ENV_CONFIG).forEach(([key, config]) => {
    if (config.required && !config.value) {
      errors.push(`Missing required environment variable: REACT_APP_${key}`);
    }
    
    // Specific validations
    switch (key) {
      case 'BACKEND_URL':
        if (config.value && !isValidUrl(config.value)) {
          errors.push(`Invalid BACKEND_URL format: ${config.value}`);
        }
        break;
        
      case 'API_TIMEOUT':
        if (config.value < 1000 || config.value > 120000) {
          warnings.push(`API_TIMEOUT should be between 1000ms and 120000ms, got: ${config.value}`);
        }
        break;
        
      case 'API_RETRY_ATTEMPTS':
        if (config.value < 0 || config.value > 10) {
          warnings.push(`API_RETRY_ATTEMPTS should be between 0 and 10, got: ${config.value}`);
        }
        break;
        
      case 'LOG_LEVEL':
        if (!['debug', 'info', 'warn', 'error'].includes(config.value)) {
          warnings.push(`Invalid LOG_LEVEL: ${config.value}. Using 'error' as fallback.`);
          ENV_CONFIG[key].value = 'error';
        }
        break;
    }
  });
  
  return { errors, warnings };
};

// URL validation helper
const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

// Environment detection
const getEnvironment = () => {
  const nodeEnv = import.meta.env.MODE;
  const hostname = window.location.hostname;
  
  if (nodeEnv === 'development' || hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  if (hostname.includes('staging') || hostname.includes('test')) {
    return 'staging';
  }
  
  return 'production';
};

// Initialize and validate environment
const initializeEnvironment = () => {
  const { errors, warnings } = validateEnvironment();
  const environment = getEnvironment();
  
  // Log validation results
  if (errors.length > 0) {
    console.error('Environment validation errors:', errors);
    if (environment === 'production') {
      throw new Error(`Environment validation failed: ${errors.join(', ')}`);
    }
  }
  
  if (warnings.length > 0) {
    console.warn('Environment validation warnings:', warnings);
  }
  
  // Log environment info in development
  if (environment === 'development' && ENV_CONFIG.DEBUG_MODE.value) {
    console.log('Environment Configuration:', {
      environment,
      config: Object.fromEntries(
        Object.entries(ENV_CONFIG).map(([key, config]) => [
          key,
          key.includes('KEY') || key.includes('SECRET') ? '[REDACTED]' : config.value
        ])
      )
    });
  }
  
  return {
    environment,
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

// Export configuration object
export const config = Object.fromEntries(
  Object.entries(ENV_CONFIG).map(([key, config]) => [key, config.value])
);

// Export environment utilities
export const environment = getEnvironment();
export const isDevelopment = environment === 'development';
export const isStaging = environment === 'staging';
export const isProduction = environment === 'production';

// Export validation results
export const environmentValidation = initializeEnvironment();

// Configuration helpers
export const getConfig = (key, fallback = null) => {
  return config[key] !== undefined ? config[key] : fallback;
};

export const isFeatureEnabled = (feature) => {
  return getConfig(feature, false) === true;
};

export const getApiConfig = () => ({
  timeout: config.API_TIMEOUT,
  retryAttempts: config.API_RETRY_ATTEMPTS,
  cacheTimeout: config.CACHE_TIMEOUT,
  enableCaching: config.ENABLE_CACHING
});

export const getImageConfig = () => ({
  maxSize: config.MAX_IMAGE_SIZE,
  supportedFormats: config.SUPPORTED_IMAGE_FORMATS.split(',').map(f => f.trim()),
  maxSizeMB: Math.round(config.MAX_IMAGE_SIZE / 1024 / 1024 * 100) / 100
});

// Debug utilities
export const debugLog = (...args) => {
  if (config.DEBUG_MODE && ['debug', 'info'].includes(config.LOG_LEVEL)) {
    console.log('[DEBUG]', ...args);
  }
};

export const infoLog = (...args) => {
  if (['debug', 'info'].includes(config.LOG_LEVEL)) {
    console.info('[INFO]', ...args);
  }
};

export const warnLog = (...args) => {
  if (['debug', 'info', 'warn'].includes(config.LOG_LEVEL)) {
    console.warn('[WARN]', ...args);
  }
};

export const errorLog = (...args) => {
  console.error('[ERROR]', ...args);
};

// Environment info for debugging
export const getEnvironmentInfo = () => ({
  environment,
  userAgent: navigator.userAgent,
  url: window.location.href,
  timestamp: new Date().toISOString(),
  config: Object.fromEntries(
    Object.entries(config).map(([key, value]) => [
      key,
      key.includes('KEY') || key.includes('SECRET') ? '[REDACTED]' : value
    ])
  )
});

// Default export
export default {
  ...config,
  environment,
  isDevelopment,
  isStaging,
  isProduction,
  getConfig,
  isFeatureEnabled,
  getApiConfig,
  getImageConfig,
  debugLog,
  infoLog,
  warnLog,
  errorLog,
  getEnvironmentInfo
};